<template>
  <div class="bg-white">
    <loading :status="loading" />
    <div class="space-x-2 flex justify-between">
      <h2 class="w-full font-bold tracking-wider uppercase">
        <span class="text-xl">{{ app.name }}</span>
        <small>
          <p v-if="app.phone">{{ app.phone }}</p>
          <p v-html="app.address"></p>
        </small>
      </h2>
    </div>
    <div class="flex items-center mt-2">
      <label class="min-w-32 block font-bold uppercase">{{ $t('Report date') }}</label>
      <span class="mr-4 inline-block">:</span>
      <div>{{ $route.query.starting_date }} 00:00:00 - {{ $route.query.starting_date }} 23:59:59</div>
    </div>
    <div class="flex items-center">
      <label class="min-w-32 block font-bold uppercase">{{ $t('Report For') }}</label>
      <span class="mr-4 inline-block">:</span>
      <div>{{ report.user }}</div>
    </div>

    <div class="w-full mt-1 py-2 overflow-hidden">
      <div class="flex items-center mt-2">
        <label class="min-w-52 block font-bold capitalize">{{ $t('Total sales') }}</label>
        <span class="mr-4 inline-block">:</span>
        <sub>{{ report.total_sales_amount | currency }} </sub><sup class="px-1">{{ report.total_sales }}</sup>
      </div>
      <div class="flex items-center">
        <label class="min-w-52 block font-bold capitalize">{{ $t('Total repairs') }}</label>
        <span class="mr-4 inline-block">:</span>
        <sub>{{ report.total_repairs_amount | currency }} </sub><sup class="px-1">{{ report.total_repairs }}</sup>
      </div>
      <div class="flex items-center">
        <label class="min-w-52 block font-bold capitalize">{{ $t('Grand amount') }}</label>
        <span class="mr-4 inline-block">:</span>
        <sub>{{ grandAmount | currency }} </sub>
      </div>

      <div class="flex items-center mt-4">
        <label class="min-w-52 block font-bold capitalize">{{ $t('Total bills') }}</label>
        <span class="mr-4 inline-block">:</span>
        <sub>{{ report.total_custom_bills_amount | currency }} </sub><sup class="px-1">{{ report.total_custom_bills }}</sup>
      </div>
      <div class="flex items-center">
        <label class="min-w-52 block font-bold capitalize">{{ $t('Total sales refunds') }}</label>
        <span class="mr-4 inline-block">:</span>
        <sub>{{ report.total_refund_amount | currency }} </sub><sup class="px-1">{{ report.total_refunds }}</sup>
      </div>

      <div class="flex items-center">
        <label class="min-w-52 block font-bold capitalize">{{ $t('Total custom purchase') }}</label>
        <span class="mr-4 inline-block">:</span>
        <sub>{{ report.total_custom_products_amount | currency }} </sub><sup class="px-1">{{ report.total_custom_products }}</sup>
      </div>
    </div>
    <div class="my-2">
      <div class="font-semibold capitalize tracking-wider text-center border-b border-t bg-gray-100 py-2 mb-1">{{ $t('Sold items') }}</div>
      <table class="min-w-full">
        <thead>
          <tr class="text-left">
            <th class="font-semibold capitalize tracking-wider">{{ $t('Item') }}</th>
            <th class="font-semibold capitalize tracking-wider">{{ $t('Total') }}</th>
          </tr>
        </thead>
        <tbody class="divide divide-gray-200">
          <tr v-for="(item, index) in report.sold_items" :key="index">
            <td class="text-left space-y-2">
              {{ item.name }}<br />
              {{ item.imei }}<br />
              {{ item.serial_number }}
            </td>
            <td>
              <sub>{{ item.qty }} x {{ item.price | currency }} </sub><sup>{{ item.sub_total | currency }}</sup>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="my-2">
      <div class="font-semibold capitalize tracking-wider text-center border-b border-t bg-gray-100 py-2 mb-1">{{ $t('Refund Sold items') }}</div>
      <table class="min-w-full">
        <thead>
          <tr class="text-left">
            <th class="font-semibold capitalize tracking-wider">{{ $t('Item') }}</th>
            <th class="font-semibold capitalize tracking-wider">{{ $t('Total') }}</th>
          </tr>
        </thead>
        <tbody class="divide divide-gray-200">
          <tr v-for="(item, index) in report.refund_items" :key="index">
            <td class="text-left space-y-2">
              {{ item.name }}<br />
              {{ item.imei }}<br />
              {{ item.serial_number }}
            </td>
            <td>
              <sub>{{ item.qty }} x {{ item.price | currency }} </sub><sup>{{ item.sub_total | currency }}</sup>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="my-4">
      <div class="font-semibold capitalize tracking-wider text-center border-b border-t bg-gray-100 py-2 mb-1">{{ $t('Repairs') }}</div>
      <table class="min-w-full">
        <thead>
          <tr class="text-left">
            <th class="font-semibold capitalize tracking-wider text-xs">{{ $t('Fault') }}</th>
            <th class="font-semibold capitalize tracking-wider text-xs">{{ $t('Item') }}</th>
            <th class="font-semibold capitalize tracking-wider text-xs">{{ $t('Charges') }}</th>
            <th class="font-semibold capitalize tracking-wider text-xs">{{ $t('Cost') }}</th>
            <th class="font-semibold capitalize tracking-wider text-xs">{{ $t('Profit') }}</th>
            <th class="font-semibold capitalize tracking-wider text-xs">{{ $t('Total') }}</th>
          </tr>
        </thead>
        <tbody class="divide divide-gray-200">
          <tr v-for="(item, index) in report.repaired_items" :key="index">
            <td>{{ item.issue }}<br /></td>
            <td>{{ item.model }}</td>
            <td>{{ item.charges | currency }}</td>
            <td>{{ item.cost | currency }}</td>
            <td>{{ item.profit | currency }}</td>
            <td>{{ item.total_amount | currency }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="my-4">
      <div class="font-semibold capitalize tracking-wider text-center border-b border-t bg-gray-100 py-2 mb-1">{{ $t('Custom Bills') }}</div>
      <table class="min-w-full">
        <thead>
          <tr class="text-left">
            <th class="font-semibold capitalize tracking-wider">{{ $t('Description') }}</th>
            <th class="font-semibold capitalize tracking-wider">{{ $t('Tracking') }}</th>
            <th class="font-semibold capitalize tracking-wider">{{ $t('Total amount') }}</th>
          </tr>
        </thead>
        <tbody class="divide divide-gray-200">
          <tr v-for="(item, index) in report.custom_bills" :key="index">
            <td>{{ item.description }}<br /></td>
            <td>{{ item.tracking }}</td>
            <td>{{ item.total_amount | currency }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="my-4">
      <div class="font-semibold capitalize tracking-wider text-center border-b border-t bg-gray-100 py-2 mb-1">{{ $t('Custom Purchases') }}</div>
      <table class="min-w-full">
        <thead>
          <tr class="text-left">
            <th class="font-semibold capitalize tracking-wider">{{ $t('Item') }}</th>
            <th class="font-semibold capitalize tracking-wider">{{ $t('IMEI') }}</th>
            <th class="font-semibold capitalize tracking-wider">{{ $t('Price') }}</th>
          </tr>
        </thead>
        <tbody class="divide divide-gray-200">
          <tr class="text-gray-900" v-for="(item, index) in report.custom_products" :key="index">
            <td>{{ item.model }}<br /></td>
            <td>{{ item.imei }}</td>
            <td>{{ item.price | currency }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
export default {
  name: 'report-card',
  metaInfo() {
    return {
      title: this.$i18n.t('Print report card'),
    };
  },
  computed: {
    grandAmount(){
      return Number(this.report.total_sales_amount + this.report.total_repairs_amount);
    },
    app() {
      return this.$store.getters['app/getSettings'];
    },
  },
  data() {
    return {
      loading: false,
      report: {
        total_sales: 0,
        total_sales_amount: 0,
        total_repairs: 0,
        total_repairs_amount: 0,
        total_custom_bills: 0,
        total_custom_bills_amount: 0,
        total_custom_products: 0,
        total_custom_products_amount: 0,
        sold_items: [],
        repaired_items: [],
        custom_bills: [],
        refund_items: [],
        custom_products: [],
      },
    };
  },
  mounted() {
    this.generateReport();
  },

  methods: {
    generateReport() {
      this.loading = true;
      axios
        .get(route('print-report-card'), { params: this.$route.query })
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.report = response.data.report;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
